<template>
  <section id="instructor-view-course" class="backend">
    <div class="container">
      <div class="row">
        <div class="col-xs-12 text-center">
          <h1 class="page-heading">View Course</h1>
          <p v-if="activeSession" class="active-session">
            <span class="active-session__date">{{ activeSession.date }}</span>
            <span class="active-session__dot" aria-hidden="true">&middot;</span>
            <span class="active-session__time">{{ activeSession.start }} to {{ activeSession.end }}</span>
          </p>
        </div>
      </div>
    </div>

    <dcLoading v-if="loading"></dcLoading>

    <div class="container" v-else>
      <div class="row">
        <div class="col-sm-12"
          :class="{ 'col-md-6': currentInstructor, 'col-md-8 col-md-offset-2': !currentInstructor }">

          <div class="well">
            <div class="card-head">
              <h2 class="well-heading mb0">Course Details</h2>
            </div>
            <dl class="course-facts">
              <div v-for="fact in facts" :key="fact.key" class="course-facts__row">
                <dt class="course-facts__label">{{ fact.label }}</dt>
                <dd class="course-facts__value">
                  <span v-if="fact.html" v-html="fact.value"></span>
                  <template v-else>{{ fact.value }}</template>
                </dd>
              </div>
            </dl>
          </div>

          <div class="well">
            <div class="card-head">
              <h2 class="well-heading mb0">Class Schedule</h2>
              <span class="card-head__count">{{ sessionCountLabel }}</span>
            </div>
            <ul class="session-rail">
              <li v-for="session in visibleSessions" :key="session.id" class="session-rail__item">
                <button type="button" class="session" :class="{
                  'session--active': session.id === activeSessionId,
                  'session--past': session.isPast && session.id !== activeSessionId
                }" @click="selectClassDate(session)">
                  <span class="session__num">{{ session.number }}</span>
                  <span class="session__body">
                    <span class="session__date">{{ session.date }}</span>
                    <span class="session__time">{{ session.start }} &ndash; {{ session.end }}</span>
                  </span>
                  <span v-if="session.id === activeSessionId" class="session__badge">Viewing</span>
                </button>
              </li>
            </ul>
            <button v-if="hasHiddenSessions" type="button" class="session-toggle"
              :aria-expanded="scheduleExpanded ? 'true' : 'false'" @click="toggleSchedule">
              {{ scheduleToggleLabel }}
              <span class="session-toggle__chevron" :class="{ 'session-toggle__chevron--up': scheduleExpanded }"
                aria-hidden="true"></span>
            </button>
          </div>
        </div>

        <div v-if="currentInstructor" class="col-sm-12 col-md-6">
          <div class="well p0">
            <div class="card-head card-head--padded">
              <h2 class="well-heading mb0">Course Roster</h2>
              <div class="card-head__actions">
                <button :disabled="!currentInstructor" @click="emitEvent('print')" class="btn btn-gray">
                  <i class="fa fa-circle-o-notch fa-spin fa-fw" v-if="printing"></i>
                  Print
                </button>
                <button data-cy="viewAttendance" class="btn btn-dc" v-if="currentInstructor"
                  :disabled="!studentsAttached" @click="goToAttendance">Attendance</button>
              </div>
            </div>
            <div class="roster" :style="rosterStyles">
              <div class="roster__row roster__row--header">
                <div v-if="showEmailForm" class="roster__cell roster__cell--head">Select</div>
                <div class="roster__cell roster__cell--head">Contract Received</div>
                <div class="roster__cell roster__cell--head">Name</div>
                <div class="roster__cell roster__cell--head">Actions</div>
              </div>

              <div class="roster__row" v-for="student in options.studentsInCourse" :key="student.id">
                <div v-if="showEmailForm" class="roster__cell roster__cell--check">
                  <input type="checkbox" v-model="selectedStudents" :value="student" />
                </div>
                <div class="roster__cell roster__cell--contract">
                  <span class="roster__label">Contract Received</span>
                  <select v-model="student.pivot.received_contract" class="form-control"
                    :aria-label="'Contract received for ' + student.fullname">
                    <option v-for="option in contractOptions" :key="option.value" :value="option.value">
                      {{ option.name }}
                    </option>
                  </select>
                </div>
                <div class="roster__cell roster__cell--name">{{ student.fullname }}</div>
                <div class="roster__cell roster__cell--actions">
                  <button type="button" data-cy="drivecoach.admin.course.roster.save"
                    @click="emitEvent('save-roster', { student_id: student.id, course_id: course.id, received_contract: student.pivot.received_contract })"
                    class="btn btn-green btn-small">Save</button>
                  <button type="button" data-cy="remove-student"
                    @click="emitEvent('remove-student', { id: student.id, fullname: student.fullname })"
                    class="btn btn-red btn-small">Remove</button>
                </div>
              </div>

              <div class="roster__row roster__row--email-all" v-if="showEmailForm">
                <div class="roster__cell roster__cell--check">
                  <input type="checkbox" id="email-all-students" v-model="allStudentsSelected" />
                </div>
                <div class="roster__cell roster__cell--email-all">
                  <label for="email-all-students">Email All Students</label>
                </div>
              </div>
            </div>

            <div v-if="showEmailForm">
              <div class="well-header" style="margin-top:2em;">
                <h2 class="well-heading mb-1">Email Students</h2>
                <p>Use this to send emails to students in this course. <strong>Select students to email above.</strong>
                </p>
              </div>
              <div class="well-content">
                <div style="padding: 1em;">
                  <dcSendEmail :courseId="course.id" :selectedUsers="selectedStudents"
                    :placeholder="'Type your message here! Send Zoom link, reminder to bring permits, etc.'"
                    @emit-event="emitEvent($event.eventName, $event.dataObject)"></dcSendEmail>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { formatDateTime, isBefore } from './../../../../common/helpers/time'

import dcLoading from './../../../../common/directives/vue-directives/loading/Loading.vue'
import dcSendEmail from '../../../../common/directives/vue-directives/send-email/SendEmail.vue'

const ATTENDANCE_STATE = 'drivecoach.instructor.dashboard.course.attendance'

export default {
  name: 'dcInstructorViewCourse',
  components: { dcLoading, dcSendEmail },
  props: {
    course: { type: Object, default: () => ({}) },
    currentUserId: { type: Number, required: true },
    studentsInCourse: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    printing: { type: Boolean, default: false }
  },
  data() {
    return {
      contractOptions: [{ name: 'Yes', value: 1 }, { name: 'No', value: 0 }],
      selectedStudents: [],
      scheduleExpanded: false
    }
  },
  methods: {
    emitEvent(eventName, data) {
      this.$emit(eventName, data)
    },
    toggleSchedule() {
      this.scheduleExpanded = !this.scheduleExpanded
    },
    goToAttendance() {
      this.emitEvent('link-click', {
        destination: ATTENDANCE_STATE,
        params: { courseId: this.course.id }
      })
    },
    selectClassDate(session) {
      // The store's date/time strings are rendered by the admin and attendance screens
      // too, so they stay in the locale format those screens were built against.
      this.$store.dispatch('SET_VIEW_COURSE', {
        id: this.course.id,
        class_id: session.id,
        instructor: session.instructor ? session.instructor : { fullname: 'None' },
        instructor_id: session.instructor_id,
        date: session.startDate.toLocaleDateString(),
        start: session.startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' }),
        end: session.endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' })
      })
      this.emitEvent('set-course-class-date', { id: session.id })
    },
    hasValue(value, isHtml) {
      if (value === null || value === undefined) {
        return false
      }
      const text = isHtml ? String(value).replace(/<[^>]*>/g, '') : String(value)
      return text.trim() !== ''
    }
  },
  computed: {
    allStudentsSelected: {
      get() {
        if (this.studentsInCourse && this.studentsInCourse.length > 0) { // A users array exists with at least one item
          let allSelected = true;
          for (const student of this.studentsInCourse) {
            if (!this.selectedStudents.includes(student)) {
              allSelected = false; // If even one is not included in array
            }
            // Break out of loop if mismatch already found
            if (!allSelected) break;
          }
          return allSelected;
        }
        return false;
      },
      set(value) {
        const selected = [];
        this.studentsInCourse.forEach((student) => {
          if (value === true) {
            selected.push(student);
          }
        });
        this.selectedStudents = selected;
      }
    },
    options() {
      return {
        // slice() first — sort() mutates, and this is a prop owned by the Angular controller.
        studentsInCourse: this.studentsInCourse.slice().sort((a, b) => a.lastname.localeCompare(b.lastname))
      }
    },
    classDates() {
      return this.course && this.course.classes ? this.course.classes : []
    },
    sessions() {
      const now = new Date()
      return this.classDates.map((classTime, index) => {
        const startDate = new Date(classTime.start)
        const endDate = new Date(classTime.end)
        const position = index + 1
        return {
          id: classTime.id,
          number: position < 10 ? '0' + position : String(position),
          date: formatDateTime(startDate, 'ddd, MM/DD/YYYY'),
          start: formatDateTime(startDate, 'h:mm a'),
          end: formatDateTime(endDate, 'h:mm a'),
          startDate,
          endDate,
          instructor: classTime.instructor,
          instructor_id: classTime.instructor_id,
          isPast: isBefore(endDate, now)
        }
      })
    },
    // The getter returns 0 unless the stored class belongs to this course, so the rail
    // can no longer pin a session from a previously viewed course. The fallback to the
    // first session covers the genuine "no class selected yet" case.
    activeSession() {
      const storedClassId = this.$store.getters['courses/classIdForCourse'](this.course.id)
      const match = this.sessions.find(session => session.id === storedClassId)
      return match || this.sessions[0] || null
    },
    activeSessionId() {
      return this.activeSession ? this.activeSession.id : null
    },
    visibleSessions() {
      if (this.scheduleExpanded || !this.activeSession) {
        return this.sessions
      }
      return [this.activeSession]
    },
    hasHiddenSessions() {
      return this.sessions.length > 1
    },
    sessionCountLabel() {
      const total = this.sessions.length
      return total === 1 ? '1 class' : total + ' classes'
    },
    scheduleToggleLabel() {
      return this.scheduleExpanded ? 'Show less' : 'Show all ' + this.sessions.length + ' classes'
    },
    // One name per instructor, not one per class date.
    instructorNames() {
      const seen = Object.create(null)
      const names = []
      this.classDates.forEach((classTime) => {
        const instructor = classTime.instructor
        if (!instructor || !instructor.fullname) {
          return
        }
        const identity = instructor.id !== null && instructor.id !== undefined
          ? 'id:' + instructor.id
          : 'name:' + instructor.fullname
        if (seen[identity]) {
          return
        }
        seen[identity] = true
        names.push(instructor.fullname)
      })
      return names
    },
    facts() {
      const course = this.course || {}
      const entries = [
        { key: 'name', label: 'Course Name', value: course.name },
        { key: 'type', label: 'Course Type', value: course.type ? course.type.name : '' },
        { key: 'seats', label: 'Number of Students', value: course.number_of_seats },
        { key: 'location', label: 'Location', value: course.location ? course.location.name : '' },
        {
          key: 'instructors',
          label: this.instructorNames.length > 1 ? 'Instructors' : 'Instructor',
          value: this.instructorNames.length ? this.instructorNames.join(', ') : 'None'
        },
        { key: 'classroom', label: 'Classroom', value: course.classroom ? course.classroom.name : '' },
        { key: 'description', label: 'Course Description', value: course.description, html: true }
      ]
      return entries.filter(fact => this.hasValue(fact.value, fact.html))
    },
    currentInstructor() {
      return !!this.activeSession && this.activeSession.instructor_id === this.currentUserId
    },
    studentsAttached() {
      return this.studentsInCourse.length > 0
    },
    showEmailForm() {
      return false; //this is just to hide it for now
    },
    rosterStyles() {
      return {
        '--roster-cols': this.showEmailForm ? '50px 100px 1fr 1fr' : '100px 1fr 1fr'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../assets/css/partials/variables.scss';

$eyebrow-color: $light-grey;

@mixin eyebrow {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: $eyebrow-color;
}

.well {
  border-radius: 8px;
  box-shadow: 0px 3px 6px #0000001D;
  border: 1px solid #F4F4F4;
  padding: 15px;
  width: 100%;
  margin-bottom: 20px;

  h2 {
    font-size: 140%;
    font-weight: 300;
    text-align: left;
  }

  .well-header {
    padding: 15px;
  }

  &.p0 {
    padding: 0;
  }
}

.active-session {
  font-size: 15px;
  color: $grey;
  font-variant-numeric: tabular-nums;

  &__dot {
    margin: 0 6px;
    color: $light-grey;
  }
}

/* ---- Course details --------------------------------------------------- */

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  &--padded {
    padding: 15px;
  }

  &__count {
    @include eyebrow;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }

  @media #{$small} {
    flex-direction: column;
    align-items: stretch;

    &__count {
      order: -1;
    }

    &__actions .btn {
      flex: 1;
      white-space: normal;
    }
  }
}

.course-facts {
  margin: 12px 0 0;

  &__row {
    padding: 10px 0;
    border-top: 1px solid $light2;

    &:first-child {
      border-top: 0;
      padding-top: 0;
    }
  }

  &__label {
    @include eyebrow;
    margin: 0 0 3px;
    font-weight: 700;
  }

  &__value {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: $grey;
    overflow-wrap: break-word;
  }
}

/* ---- Class schedule (the session rail) -------------------------------- */

.session-rail {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;

  &__item+&__item {
    border-top: 1px solid $light2;
  }
}

.session {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 9px 8px 9px 5px;
  border: 0;
  border-left: 3px solid transparent;
  background: none;
  text-align: left;
  color: $grey;

  &:hover {
    background: rgba($green, 0.06);
    cursor: pointer;
  }

  &:focus {
    outline: 2px solid $green;
    outline-offset: -2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &__num {
    @include eyebrow;
    min-width: 28px;
    padding: 5px 6px;
    border-radius: 3px;
    background: $light2;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  &__body {
    display: flex;
    flex-direction: column;
    min-width: 0;

    @media #{$sevensixeight} {
      flex-direction: row;
      align-items: baseline;
      gap: 10px;
    }
  }

  &__date {
    font-size: 13px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  &__time {
    font-size: 12px;
    color: $light-grey;
    font-variant-numeric: tabular-nums;
  }

  &__badge {
    @include eyebrow;
    padding: 4px 8px;
    border-radius: 3px;
    background: lighten($green, 32%);
    color: $dark-green;
    white-space: nowrap;
  }

  &--active {
    border-left-color: $green;
    background: rgba($green, 0.07);

    .session__num {
      background: $green;
      color: $white;
    }

    .session__date {
      font-weight: 700;
    }
  }

  &--past {
    .session__num {
      opacity: 0.55;
    }

    .session__date,
    .session__time {
      color: $light-grey;
    }
  }
}

.session-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin-top: 4px;
  padding: 12px;
  border: 0;
  border-top: 1px solid $light2;
  background: none;
  color: $dark-green;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;

  &:hover {
    background: rgba($green, 0.06);
    cursor: pointer;
  }

  &:focus {
    outline: 2px solid $green;
    outline-offset: -2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &__chevron {
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid $dark-green;

    @media (prefers-reduced-motion: no-preference) {
      transition: transform 0.15s ease-in-out;
    }

    &--up {
      transform: rotate(180deg);
    }
  }
}

/* ---- Roster ------------------------------------------------------------ */

.roster {
  display: grid;
  grid-template-columns: var(--roster-cols, 100px 1fr 1fr);

  &__row {
    display: contents;
  }

  &__cell {
    display: flex;
    align-items: center;
    padding: 4px 7px;
    border: 1px solid $light;

    &--head {
      @include eyebrow;
      background: $black;
      color: $white;
      border: 0;
      border-right: 1px solid $white;

      &:last-child {
        border-right: 0;
      }
    }

    &--actions {
      display: block;
    }

    &--email-all {
      grid-column: 2 / -1;
    }
  }

  &__label {
    display: none;
  }

  @media #{$small} {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;

    .roster__row {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 14px;
      border: 1px solid $light2;
      border-radius: 4px;
      background: $white;
      box-shadow: 0 1px 2px rgba($black, 0.04);

      &--header {
        display: none;
      }
    }

    .roster__cell {
      display: block;
      padding: 0;
      border: 0;

      &--check {
        order: 0;
      }

      &--name {
        order: 1;
        font-size: 14px;
        font-weight: 700;
        color: $grey;
      }

      &--contract {
        order: 2;
      }

      &--email-all {
        order: 2;
      }

      &--actions {
        order: 3;
        display: flex;
        gap: 8px;

        .btn {
          flex: 1;
          white-space: normal;
        }
      }
    }

    .roster__label {
      @include eyebrow;
      display: block;
      margin-bottom: 4px;
    }
  }
}
</style>
